import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../store/auth'

// Import views
import HomeView from '../views/Home.vue'
import LoginView from '../views/auth/Login.vue'
import RegisterView from '../views/auth/Register.vue'
import Verify2FAView from '../views/auth/Verify2FA.vue'
import ProfileView from '../views/auth/Profile.vue'
import Setup2FAView from '../views/auth/Setup2FA.vue'
import ChangePasswordView from '../views/auth/ChangePassword.vue'
import UsersView from '../views/UsersView.vue'
import DashboardView from '../views/Dashboard.vue'
import CustomersView from '../views/CustomersView.vue'
import CustomerProfileView from '../views/CustomerProfileView.vue'
import CompleteSignupView from '../views/auth/CompleteSignup.vue'
import OrganisationsView from '../views/OrganisationsView.vue'
import OrganisationDetailView from '../views/OrganisationDetailView.vue'
import AssociatesView from '../views/AssociatesView.vue'
import FeatureFlagsView from '../views/FeatureFlagsView.vue'
import PoliciesView from '../views/PoliciesView.vue'
import WelcomeView from '../views/WelcomeView.vue'
import PortalView from '../views/PortalView.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore()
      if (authStore.isAuthenticated) {
        next({ name: 'Dashboard' })
      } else {
        next({ name: 'Login' })
      }
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { guestOnly: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterView,
    meta: { requiresAuth: true }
  },
  {
    path: '/verify-2fa',
    name: 'Verify2FA',
    component: Verify2FAView,
    meta: { guestOnly: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfileView,
    meta: { requiresAuth: true }
  },
  {
    path: '/setup-2fa',
    name: 'Setup2FA',
    component: Setup2FAView,
    meta: { requiresAuth: true }
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: ChangePasswordView,
    meta: { requiresAuth: true }
  },
  {
    path: '/users',
    name: 'Users',
    component: UsersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: { requiresAuth: true }
  },
  {
    path: '/manuals',
    name: 'Manuals',
    component: () => import('../views/ManualsView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/manuals/review',
    name: 'ManualReview',
    component: () => import('../views/ManualReviewView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/manuals/pages/:id',
    name: 'ManualPage',
    component: () => import('../views/ManualPageView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/customers',
    name: 'Customers',
    component: CustomersView,
    meta: { requiresAuth: true }
  },
  {
    path: '/customers/:id',
    name: 'CustomerProfile',
    component: CustomerProfileView,
    meta: { requiresAuth: true }
  },
  {
    path: '/organisations',
    name: 'Organisations',
    component: OrganisationsView,
    meta: { requiresAuth: true, requiresOrganisationAccess: true }
  },
  {
    path: '/organisations/:id',
    name: 'OrganisationDetail',
    component: OrganisationDetailView,
    meta: { requiresAuth: true, requiresOrganisationAccess: true }
  },
  {
    path: '/associates',
    name: 'Associates',
    component: AssociatesView,
    meta: { requiresAuth: true, requiresOrganisationAccess: true }
  },
  {
    path: '/feature-flags',
    name: 'FeatureFlags',
    component: FeatureFlagsView,
    meta: { requiresAuth: true, requiresOrganisationAccess: true }
  },
  {
    path: '/signup/:token',
    name: 'CompleteSignup',
    component: CompleteSignupView,
    meta: { public: true, guestOnly: true, noLayout: true }
  },
  {
    path: '/policies',
    name: 'Policies',
    component: PoliciesView,
    meta: { requiresAuth: true, requiresOrganisationAccess: true }
  },
  {
    path: '/welcome/:token',
    name: 'Welcome',
    component: WelcomeView,
    meta: { public: true, noLayout: true }
  },
  {
    path: '/portal/:slug?',
    name: 'Portal',
    component: PortalView,
    meta: { public: true, noLayout: true }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Special handling for CompleteSignup - skip auth check completely
  if (to.name === 'CompleteSignup' || to.path.startsWith('/signup/')) {
    return next()
  }
  
  // Check if user is already loaded, if not try to load
  if (!authStore.isAuthenticated && !authStore.loading) {
    try {
      await authStore.fetchCurrentUser()
    } catch (error) {
      // User is not authenticated
    }
  }
  
  // Allow public routes to pass through without auth check
  if (to.matched.some(record => record.meta.public)) {
    return next()
  }
  
  // Handle pending 2FA routes - only redirect if not going to login page
  if (authStore.isPending2FA && to.name !== 'Verify2FA' && to.name !== 'Login') {
    return next({ name: 'Verify2FA' })
  }
  
  // Handle auth-required routes
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isAuthenticated) {
      return next({ name: 'Login' })
    }
  }

  // Handle organisation access check
  if (to.matched.some(record => record.meta.requiresOrganisationAccess)) {
    if (!authStore.canManageOrganisations) {
      return next({ name: 'Dashboard' })
    }
  }

  // Handle guest-only routes
  if (to.matched.some(record => record.meta.guestOnly)) {
    if (authStore.isAuthenticated && to.name !== 'CompleteSignup') {
      return next({ name: 'Dashboard' })
    }
  }

  next()
})

export default router